import { useState, useEffect } from "react";
import { FiX, FiSearch, FiUserPlus, FiCheck } from "react-icons/fi";
import toast from "react-hot-toast";
import api from "../../utils/api";
import useChatStore from "../../store/chatStore";
import { useSocket } from "../../context/SocketContext";
import { getInitials } from "../../utils/helpers";
import styles from "./AddMemberModal.module.css";

const AddMemberModal = ({ onClose }) => {
  const { activeConversation, addGroupMember } = useChatStore();
  const socketRef = useSocket();
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);

  // Current participant IDs to exclude
  const currentIds = new Set(
    (activeConversation?.participants || []).map((p) => p._id || p)
  );

  useEffect(() => {
    const fetchUsers = async () => {
      if (!search.trim()) { setUsers([]); return; }
      setLoading(true);
      try {
        const res = await api.get(`/users/search?q=${encodeURIComponent(search)}`);
        setUsers(res.data.users || []);
      } catch { setUsers([]); }
      finally { setLoading(false); }
    };
    const t = setTimeout(fetchUsers, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Also show friends not yet in group even without search
  const [friends, setFriends] = useState([]);
  useEffect(() => {
    api.get("/users/friends").then((res) => {
      setFriends((res.data.friends || []).filter((f) => !currentIds.has(f._id)));
    }).catch(() => {});
  }, []);

  const displayed = search.trim()
    ? users.filter((u) => !currentIds.has(u._id))
    : friends;

  const handleAdd = async (userId, userName) => {
    setAdding(userId);
    try {
      const updated = await addGroupMember(activeConversation._id, userId);
      socketRef?.current?.emit("group:update", {
        conversationId: activeConversation._id,
        conversation: updated,
      });
      // Emit join activity + newUserId so backend auto-joins them to socket room
      socketRef?.current?.emit("group:member_joined", {
        conversationId: activeConversation._id,
        userName,
        newUserId: userId,
      });
      toast.success(`${userName} added to group`);
      onClose();
    } catch { toast.error("Failed to add member"); }
    finally { setAdding(null); }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <button className={styles.closeBtn} onClick={onClose}><FiX size={17} /></button>
          <span className={styles.title}>Add member</span>
        </div>

        <div className={styles.searchWrap}>
          <FiSearch size={15} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        {!search.trim() && (
          <div className={styles.sectionLabel}>Friends not in group</div>
        )}

        <div className={styles.list}>
          {loading && <p className={styles.hint}>Searching...</p>}
          {!loading && displayed.length === 0 && (
            <p className={styles.hint}>
              {search.trim() ? "No users found" : "No friends to add"}
            </p>
          )}
          {displayed.map((u) => (
            <div key={u._id} className={styles.item}>
              <div className="avatar" style={{ width: 42, height: 42, fontSize: 14, flexShrink: 0 }}>
                {u.avatar ? <img src={u.avatar} alt={u.name} /> : getInitials(u.name)}
              </div>
              <div className={styles.info}>
                <span className={styles.name}>{u.name}</span>
                {u.bio && <span className={styles.bio}>{u.bio}</span>}
              </div>
              <button
                className={styles.addBtn}
                onClick={() => handleAdd(u._id, u.name)}
                disabled={adding === u._id}
                title="Add to group"
              >
                {adding === u._id ? <FiCheck size={15} /> : <FiUserPlus size={15} />}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AddMemberModal;
